import { useEffect, useRef, useCallback } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";

import Navbar from "@/components/site/Navbar";
import BackToTop from "@/components/site/BackToTop";
import Home from "@/pages/Home";
import ServicePage from "@/pages/ServicePage";

function AppShell() {
  const lenisRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;

    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  const scrollToSection = useCallback((id) => {
    const target = id === "hero" ? 0 : `#${id}`;
    if (lenisRef.current) {
      lenisRef.current.scrollTo(target, { offset: -72 });
    } else {
      const el = id === "hero" ? null : document.getElementById(id);
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 72;
        window.scrollTo({ top: y, behavior: "smooth" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  }, []);

  const scrollTop = useCallback(() => {
    if (lenisRef.current) lenisRef.current.scrollTo(0);
    else window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const onNav = useCallback(
    (id) => {
      if (location.pathname === "/") scrollToSection(id);
      else navigate(`/#${id}`);
    },
    [location.pathname, navigate, scrollToSection]
  );

  useEffect(() => {
    if (location.pathname === "/") {
      if (location.hash) {
        const id = location.hash.slice(1);
        setTimeout(() => scrollToSection(id), 140);
      }
    } else {
      if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash, scrollToSection]);

  return (
    <div className="App min-h-screen bg-brand-navy font-body antialiased">
      <Navbar onNav={onNav} />

      <Routes>
        <Route path="/" element={<Home onNav={onNav} />} />
        <Route
          path="/vyroba-klicu"
          element={<ServicePage slug="vyroba-klicu" onNav={onNav} />}
        />
        <Route
          path="/autoklice"
          element={<ServicePage slug="autoklice" onNav={onNav} />}
        />
        <Route
          path="/zamky-a-vlozky"
          element={<ServicePage slug="zamky-a-vlozky" onNav={onNav} />}
        />
        <Route
          path="/nouzove-otevirani"
          element={<ServicePage slug="nouzove-otevirani" onNav={onNav} />}
        />
        <Route path="*" element={<Home onNav={onNav} />} />
      </Routes>

      <BackToTop onClick={scrollTop} />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}
